import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, MapPin, Package, User as UserIcon } from 'lucide-react';
import type { EquipementCriticite, EquipementStatut, EquipementType } from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { CriticiteBadge, StatutEquipementBadge } from './badges';

/**
 * Panel liste des équipements rattachés à un service OU à un bâtiment.
 *
 * Réutilisé par `ServiceDetailDrawer` (onglet « Équipements ») et
 * `BatimentDetailDrawer` (onglet « Équipements ») — permet de visualiser
 * directement le parc au sein du périmètre (US client : accès équipements
 * depuis les onglets bâtiments / services).
 *
 * Branché sur le filtre existant `GET /equipements?service_id=` /
 * `?batiment_id=`. Lecture seule.
 */

interface EquipementRow {
  id: string;
  code_equipement: string;
  designation: string;
  type: EquipementType;
  statut: EquipementStatut;
  criticite: EquipementCriticite | null;
  service: { id: string; nom_complet: string } | null;
  salle: { id: string; nom: string } | null;
  utilisateur_affecte: { id: string; first_name: string; last_name: string } | null;
}

export function EquipementsListPanel({
  serviceId,
  batimentId,
  onEquipementClick,
}: {
  serviceId?: string;
  batimentId?: string;
  /**
   * Optionnel : rend chaque ligne cliquable pour ouvrir le détail de
   * l'équipement (navigation croisée service/bâtiment → équipement).
   * Si absent, la liste reste en lecture seule.
   */
  onEquipementClick?: (equipementId: string) => void;
}) {
  const [items, setItems] = useState<EquipementRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const scopeParam = serviceId
    ? `service_id=${serviceId}`
    : batimentId
      ? `batiment_id=${batimentId}`
      : null;

  useEffect(() => {
    if (!scopeParam) return;
    setLoading(true);
    setErrorMessage(null);
    api
      .get<{ data: EquipementRow[]; pagination: { total: number } }>(
        `/equipements?${scopeParam}&page_size=200&sort_by=code_equipement&sort_order=asc`,
      )
      .then((res) => {
        setItems(res.data.data);
        setTotal(res.data.pagination?.total ?? res.data.data.length);
      })
      .catch((err) => setErrorMessage(extractErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [scopeParam]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          Aucun équipement rattaché à {serviceId ? 'ce service' : 'ce bâtiment'}.
        </p>
      ) : (
        <>
          <p className="text-xs text-gray-500">
            {total} équipement{total > 1 ? 's' : ''}
            {total > items.length && ` (${items.length} affichés)`}
          </p>
          <ul className="space-y-2">
            {items.map((eq) => {
              const inner = (
                <>
                  <p className="text-sm font-medium text-gray-900 flex items-center flex-wrap gap-2">
                    <Package className="w-3.5 h-3.5 text-primary-500 flex-shrink-0" />
                    <span className="text-primary-600 text-xs font-semibold uppercase">
                      {eq.code_equipement}
                    </span>
                    {eq.designation}
                    <StatutEquipementBadge statut={eq.statut} />
                    {eq.criticite && <CriticiteBadge criticite={eq.criticite} />}
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                    {/* En vue bâtiment on montre le service ; en vue service on montre la salle. */}
                    {serviceId
                      ? eq.salle && (
                          <span className="flex items-center">
                            <MapPin className="w-3 h-3 mr-1 text-gray-400" />
                            {eq.salle.nom}
                          </span>
                        )
                      : eq.service && (
                          <span className="flex items-center">
                            <MapPin className="w-3 h-3 mr-1 text-gray-400" />
                            {eq.service.nom_complet}
                          </span>
                        )}
                    {eq.utilisateur_affecte && (
                      <span className="flex items-center">
                        <UserIcon className="w-3 h-3 mr-1 text-gray-400" />
                        {eq.utilisateur_affecte.first_name} {eq.utilisateur_affecte.last_name}
                      </span>
                    )}
                  </div>
                </>
              );
              return (
                <li key={eq.id}>
                  {onEquipementClick ? (
                    <button
                      type="button"
                      onClick={() => onEquipementClick(eq.id)}
                      className="w-full text-left rounded-lg border border-gray-200 p-3 bg-white hover:border-primary-300 hover:bg-primary-50/40 transition-colors"
                    >
                      {inner}
                    </button>
                  ) : (
                    <div className="rounded-lg border border-gray-200 p-3 bg-white">{inner}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
